import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { checkRemainingTestTime } from "../../../utils/timeUtils";
import { submitTestApi } from "../../../services/saveAndNext";
import { Store } from "../../../store";

const IitSectionTimer = (props) => {
  const { t } = useTranslation();
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const intervalRef = useRef(null);
  const apiPollRef = useRef(null);

  const apiTimeRef = useRef(0); // store API time in seconds
  const apiFetchedAtRef = useRef(0); // store when the time was fetched

  const formatTime = (seconds) =>
    Math.floor(seconds / 3600) +
    " : " +
    ("0" + (Math.floor(seconds / 60) % 60)).slice(-2) +
    " : " +
    ("0" + (seconds % 60)).slice(-2);

  const setSessionHeaders = () => {
    const student = props?.state?.student_data;
    const test = props?.state?.test_data;
    const account = props?.state?.institute_data;
    if (student && test && account) {
      const raw = `${student.userid}:${test.test_id}:${account.account_id}:${props?.state?.session_key}`;
      return btoa(raw);
    }
    return "";
  };

  // Use Store.getState() inside helper so we always read the latest Redux store
  const getCurrentSectionId = () => {
    const stateNow = Store.getState();
    const { is_preview } = stateNow?.student_data || {};
    const { is_sec_time } = stateNow?.test_data || {};

    if (is_preview === 1 && is_sec_time === 1) {
      return (
        stateNow?.selectedSection ||
        `${stateNow?.subjects_data?.[0]?.subject_id}#${stateNow?.subjects_data?.[0]?.section_id}`
      );
    }

    const activeQ = stateNow?.ques_data?.[stateNow?.activeQuesInd];
    return activeQ ? `${activeQ.subject_id}#${activeQ.section_id}` : null;
  };

  /**
   * Robust section switch:
   * - Always read latest store when deciding whether we truly need to switch.
   * - Only dispatch ACTIVE_QUESTION_NUMBER if restoreIndex is valid AND different from current active index,
   *   unless forceSwitch === true (server/time-driven switch).
   */
  const handleSectionSwitch = (nextSectionId, forceSwitch = false) => {
    const stateNow = Store.getState();
    const ques_data = stateNow?.ques_data || [];
    const activeQuesInd = stateNow?.activeQuesInd;
    const activeQuesIndBySection = stateNow?.activeQuesIndBySection || {};

    const currentQ = ques_data[activeQuesInd];
    const currentSectionId = currentQ ? `${currentQ.subject_id}#${currentQ.section_id}` : null;

    // If already in same section and not forcing, do nothing
    if (!forceSwitch && currentSectionId === nextSectionId) {
      // console.log("✅ handleSectionSwitch: already inside section, skipping switch:", nextSectionId);
      return;
    }

    // Update selected section in store (keep this)
    props.dispatch({ type: "SELECTED_SUBJECT", selectedSection: nextSectionId });

    // compute restore index using latest store
    setTimeout(() => {
      const stateThen = Store.getState();
      const ques_data_then = stateThen?.ques_data || [];
      const activeIndexNow = stateThen?.activeQuesInd;

      // prefer a per-section preserved index, but validate it belongs to the desired section
      const candidateIndex = activeQuesIndBySection?.[nextSectionId];
      let restoreIndex = -1;
      if (
        Number.isInteger(candidateIndex) &&
        candidateIndex >= 0 &&
        ques_data_then[candidateIndex] &&
        `${ques_data_then[candidateIndex].subject_id}#${ques_data_then[candidateIndex].section_id}` ===
          nextSectionId
      ) {
        restoreIndex = candidateIndex;
      } else {
        // fallback: first question index of that section
        restoreIndex = ques_data_then.findIndex(
          (q) => `${q.subject_id}#${q.section_id}` === nextSectionId
        );
      }

      if (restoreIndex === -1) {
        console.warn("handleSectionSwitch: couldn't find a question for", nextSectionId);
        return;
      }

      // If we already are on this index and we are not forcing, don't dispatch
      if (!forceSwitch && restoreIndex === activeIndexNow) {
        // console.log("✅ handleSectionSwitch: restoreIndex equals current active index — no dispatch.", {
        //   restoreIndex,
        //   activeIndexNow,
        // });
        return;
      }

      // Dispatch only when necessary (or forced)
      props.dispatch({
        type: "ACTIVE_QUESTION_NUMBER",
        activeQuesInd: restoreIndex,
      });
      // console.log("🚨 Dispatch ACTIVE_QUESTION_NUMBER", restoreIndex, "from <iit>");
    }, 300);
  };

  const handleTimeUp = () => {
    clearInterval(intervalRef.current);
    clearInterval(apiPollRef.current);
    submitTestApi(props);
  };

  const startTimerCountdown = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    intervalRef.current = setInterval(() => {
      try {
        const elapsed = Math.floor((Date.now() - apiFetchedAtRef.current) / 1000);
        const syncedTime = apiTimeRef.current - elapsed;

        if (syncedTime <= 0) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          const currentSectionId = getCurrentSectionId();
          const currentIndex = props.allSection?.findIndex((id) => id === currentSectionId);
          const nextSectionId = props.allSection?.[currentIndex + 1];

          // When the timer bites at runtime we must force the switch if applicable
          if (
            props.state.test_data?.is_neet_test === 1 &&
            props.state.test_data?.is_sec_time === 1 &&
            nextSectionId
          ) {
            handleSectionSwitch(nextSectionId, true);
            return;
          }

          handleTimeUp();
        } else {
          setRemainingSeconds(syncedTime);
        }
      } catch (err) {
        console.error("⛔ Error in timer countdown interval:", err);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);
  };

  const updateTimerWithApi = async () => {
    // Guard: stop polling if test is already submitted
    const currentState = Store.getState();
    if (currentState.testSubmitted === true) {
      clearInterval(intervalRef.current);
      clearInterval(apiPollRef.current);
      intervalRef.current = null;
      apiPollRef.current = null;
      return;
    }

    const test = props.state.test_data;
    const user = props.state.student_data;
    const isPreview = user?.is_preview === 1 || user?.userid === 1234567890;
    const isSectionWise = test?.is_sec_time === 1;

    let retries = 10;
    const waitForReduxAndSubmit = () => {
      const state = Store.getState();
      let { questionstatus, answers_json, ques_data } = state;

      if (!Array.isArray(questionstatus)) {
        try {
          if (Array.isArray(ques_data)) {
            questionstatus = ques_data.map((q) => ({ ques_id: q.ques_id, status_number: 1 }));
          } else if (Array.isArray(answers_json)) {
            questionstatus = Array.from({ length: answers_json.length }, () => ({ ques_id: 0, status_number: 1 }));
          } else {
            throw new Error("Missing ques_data and answers_json");
          }
          state.questionstatus = questionstatus;
        } catch (e) {
          console.error("❌ Failed to rebuild questionstatus:", e);
        }
      }

      if (Array.isArray(questionstatus) && Array.isArray(answers_json)) {
        submitTestApi({ ...props, state });
      } else if (retries > 0) {
        retries--;
        console.warn(`⏳ Redux not ready. Retrying (${10 - retries}/10)...`);
        setTimeout(waitForReduxAndSubmit, 300);
      } else {
        console.error("❌ Failed to submit test: Redux state never became ready.", { questionstatus, answers_json });
      }
    };

    // preview code path
    if (isPreview) {
      if (isSectionWise) {
        const currentSectionId = getCurrentSectionId();
        const section = props.state.subjects_data.find((s) => `${s.subject_id}#${s.section_id}` === currentSectionId);
        if (section?.section_time) {
          const timeInSec = parseInt(section.section_time) * 60;
          apiTimeRef.current = timeInSec;
          apiFetchedAtRef.current = Date.now();
          setRemainingSeconds(timeInSec);
        }
      } else {
        const testDuration = parseInt(test?.duration);
        if (!isNaN(testDuration)) {
          const timeInSec = testDuration * 60;
          apiTimeRef.current = timeInSec;
          apiFetchedAtRef.current = Date.now();
          setRemainingSeconds(timeInSec);
        }
      }
      return;
    }

    const testId = test?.test_id;
    const userId = user?.userid;
    const sessionKey = setSessionHeaders();

    try {
      const response = await checkRemainingTestTime({ testId, userId, sessionKey });

      if (response?.is_test_expired || response?.test_left_time === "0:00") {
        clearInterval(intervalRef.current);
        clearInterval(apiPollRef.current);
        submitTestApi(props);
        return;
      }

      if (
        response?.is_neet_test === 1 &&
        response?.is_sec_time === 1 &&
        Array.isArray(response.subjects)
      ) {
        // Use Store.getState() to compute current section reliably
        const currentSectionId = getCurrentSectionId();
        const validSections = response.subjects.filter((s) => typeof s.remain_time === "string" && s.remain_time.includes(":"));
        const allSectionsZero = validSections.every((s) => s.remain_time === "0:0");

        if (validSections.length > 0 && allSectionsZero) {
          clearInterval(intervalRef.current);
          clearInterval(apiPollRef.current);
          waitForReduxAndSubmit();
          return;
        }

        const currentSection = response.subjects.find((s) => `${s.subject_id}#${s.section_id}` === currentSectionId);

        if (currentSection?.remain_time && currentSection.remain_time !== "0:0") {
          const [min, sec] = currentSection.remain_time.split(":").map(Number);
          const timeInSec = (min || 0) * 60 + (sec || 0);
          apiTimeRef.current = timeInSec;
          apiFetchedAtRef.current = Date.now();
          setRemainingSeconds(timeInSec);
          return;
        }

        // if current section has 0 time, look for next section with time
        const currentIndex = response.subjects.findIndex((s) => `${s.subject_id}#${s.section_id}` === currentSectionId);
        const nextValid = response.subjects.slice(currentIndex + 1).find((s) => s.remain_time !== "0:0");

        if (nextValid) {
          const nextId = `${nextValid.subject_id}#${nextValid.section_id}`;
          // forced switch because server told us current is done => move forward
          handleSectionSwitch(nextId, true);
        } else {
          handleTimeUp();
        }
        return;
      }

      if (response?.is_sec_time === 0 && response?.test_left_time) {
        const [min, sec] = response.test_left_time.split(":").map(Number);
        const timeInSec = (min || 0) * 60 + (sec || 0);
        apiTimeRef.current = timeInSec;
        apiFetchedAtRef.current = Date.now();
        setRemainingSeconds(timeInSec);
        return;
      }
    } catch (err) {
      console.error("Timer error:", err);
    }
  };

  useEffect(() => {
    const user = props.state.student_data;
    if (user?.is_preview === 1 || user?.userid === 1234567890) return;
    if (props.state.timerUpdateFlag >= 1) {
      updateTimerWithApi();
    }
  }, [props.state.timerUpdateFlag]);

  useEffect(() => {
    const isSectionWise = props.state.test_data?.is_sec_time === 1;
    const selected = props.state.selectedSection;
    if (isSectionWise && selected) {
      const timeout = setTimeout(() => updateTimerWithApi(), 300);
      return () => clearTimeout(timeout);
    }
  }, [props.state.selectedSection]);

  useEffect(() => {
    const isSectionWise = props.state.test_data?.is_sec_time === 1;

    if (!isSectionWise) {
      const timer = setTimeout(() => {
        updateTimerWithApi();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const user = props.state.student_data;
    if (user?.is_preview === 1 || user?.userid === 1234567890) return;

    apiPollRef.current = setInterval(() => {
      try {
        updateTimerWithApi();
      } catch (err) {
        console.error("⛔ API polling error:", err);
        clearInterval(apiPollRef.current);
        apiPollRef.current = null;
      }
    }, 30000);

    return () => {
      clearInterval(apiPollRef.current);
      apiPollRef.current = null;
    };
  }, []);

  // Clear all intervals when test is submitted
  useEffect(() => {
    if (props.state.testSubmitted === true) {
      clearInterval(intervalRef.current);
      clearInterval(apiPollRef.current);
      intervalRef.current = null;
      apiPollRef.current = null;
    }
  }, [props.state.testSubmitted]);

  useEffect(() => {
    if (remainingSeconds > 0) {
      startTimerCountdown();
    }
  }, [remainingSeconds]);

  return (
    <div className="timerDiv">
      <span className="timeHeader">{t('test.timeLeft')} : </span>
      <div style={{ display: "flex" }}>
        {formatTime(remainingSeconds)
          .split(" : ")
          .map((val, idx) => (
            <React.Fragment key={idx}>
              <div className="timerun">{val}</div>
              {idx < 2 && <span style={{ margin: "0 5px" }}>:</span>}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(IitSectionTimer);
